/**
 * Dynamic Headline Component - Cycling Headlines & Sublines
 *
 * DEVELOPMENT LOG: Phase 3 - Dynamic Content & Educational System
 * Created: Cycling headline system with 3 variations
 * Status: 🚧 In progress
 */

import React, { useState, useEffect } from 'react';
import { DebugLabel } from '../shared';

interface HeadlineVariation {
  headline: string;
  subline: string;
}

const headlineVariations: HeadlineVariation[] = [
  {
    headline: "Test Your Prompts Across 6 AI Models Instantly",
    subline: "Run one prompt across GPT-4, Claude, Gemini, and more—get scored results with a clear winner in ~60 seconds"
  },
  {
    headline: "Compare AI Responses. Find the Perfect Model.",
    subline: "Stop guessing which AI works best. Get instant side-by-side comparisons with detailed performance scoring"
  },
  {
    headline: "One Prompt, Multiple AIs, Clear Winner",
    subline: "Discover which AI model delivers the best results for your specific use case in under a minute"
  }
];

export const DynamicHeadline: React.FC = () => {
  const [currentVariation, setCurrentVariation] = useState<HeadlineVariation>(() => {
    // Randomize selection on page load
    const randomIndex = Math.floor(Math.random() * headlineVariations.length);
    return headlineVariations[randomIndex];
  });

  const [isVisible, setIsVisible] = useState(true);

  // Optional: Cycle through variations every 8 seconds (for demo purposes)
  useEffect(() => {
    if (import.meta.env.DEV) {
      const interval = setInterval(() => {
        setIsVisible(false);

        setTimeout(() => {
          const currentIndex = headlineVariations.findIndex(
            v => v.headline === currentVariation.headline
          );
          const nextIndex = (currentIndex + 1) % headlineVariations.length;
          setCurrentVariation(headlineVariations[nextIndex]);
          setIsVisible(true);
        }, 200); // Brief fade out
      }, 8000); // Change every 8 seconds in development

      return () => clearInterval(interval);
    }
  }, [currentVariation]);

  return (
    <div className="text-center mb-12" data-testid="dynamic-headline">
      <DebugLabel label="DynamicHeadline-Component" type="component" position="top-right" />

      <div
        className={`transition-opacity duration-200 ${isVisible ? 'opacity-100' : 'opacity-70'}`}
      >
        <h1 className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-6">
          {currentVariation.headline}
        </h1>
        <p className="text-xl text-slate-300 mb-8 leading-relaxed max-w-3xl mx-auto">
          {currentVariation.subline}
        </p>
      </div>

      {/* Development indicator showing current variation */}
      {import.meta.env.DEV && (
        <div className="mt-4 text-xs text-slate-500">
          Variation {headlineVariations.findIndex(v => v.headline === currentVariation.headline) + 1} of {headlineVariations.length}
          {import.meta.env.DEV && " • Auto-cycling in dev mode"}
        </div>
      )}
    </div>
  );
};